import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import { MediaItem } from '../../stores/reducers/media';
import { ShotFeed } from '../../stores/reducers/shots';
import { GreenroomState } from '../../stores/types/types';
import {AUDIO_TYPES, IMG_TYPES, VID_TYPES} from '../../types/constants';
import AudioOnlyFeedsIcon from '../general-components/audio-only-feeds-icon';
import VideoThumbnailMouseover from './video-thumbnail-mouseover';

interface MyProps {
	children: any;
	includeSelectionBox: boolean;
	isCurrentActiveShot: boolean;
	mediaOnCanvasIds: (string | number)[];
	shotFeed: ShotFeed;
	mediaName?: string;
}

export default function VideoThumbnail(props: MyProps) {
	const {
		children,
		includeSelectionBox,
		isCurrentActiveShot,
		mediaOnCanvasIds,
		shotFeed,
		mediaName = '',
	} = props;

	const media = useSelector(
		(state: GreenroomState) => state.MediaReducer.media,
	);

	const mediaNameRef = useRef<HTMLDivElement>(null);

	const getMediaStyle = (mediaItem: MediaItem | undefined) => ({
		backgroundColor: mediaItem?.media_data?.background_color
			?? (
				mediaItem
					? '#000000'
					: 'transparent'
			),
	});

	const mediaItem = media.find((m) => m.id === shotFeed.media);
	const [mouseIsOver, setMouseIsOver] = useState<boolean>(false);

	const totalPages = useMemo((): number => (
		mediaItem?.num_pages ?? 0
	), [mediaItem?.num_pages]);

	const updateMouseOver = (isOver: boolean) => {
		if (isOver) {
			setMouseIsOver(true);
		} else {
			setMouseIsOver(false);
		}
	};

	return (
		<div className='shot-drawer-thumbnail'>
			<div
				className="video-thumb"
				onMouseEnter={() => {
					if (!IMG_TYPES.includes(mediaItem?.type ?? '')) {
						updateMouseOver(true);
					}
				}}
				onMouseLeave={() => {
					if (!IMG_TYPES.includes(mediaItem?.type ?? '')) {
						updateMouseOver(false);
					}
				}}
			>
				<div
					style={getMediaStyle(mediaItem)}
					className={classNames(
						'video-wrapper',
						{ 'no-selection-border': shotFeed.audioOnly },
						{ 'fit-thumbnail': !mediaItem?.media_data?.fill },
					)}
				>
					{children}
					<CSSTransition
						in={!!mediaName && !mouseIsOver}
						nodeRef={mediaNameRef}
						timeout={250}
						classNames="fade-in-out"
						unmountOnExit
					>
						<div className="media-name" ref={mediaNameRef}>
							<label className="font-size-14 font-weight-heavy">
								{mediaName}
							</label>
						</div>
					</CSSTransition>
					<div className="video-overlay">
						{
							includeSelectionBox
							&& (
								isCurrentActiveShot
								|| mediaOnCanvasIds.includes(shotFeed?.media ?? 0)
								|| mediaOnCanvasIds.includes(shotFeed?.presenter ?? 0)
							) && !shotFeed.audioOnly && (
								<div className="selection-border in-shot" />
							)
						}
						{isCurrentActiveShot && AUDIO_TYPES.includes(mediaItem?.type ?? '') && (
							<div className="selection-border in-shot" />
						)}
						{shotFeed.audioOnly && (
							<AudioOnlyFeedsIcon
								isCurrentActiveShot={isCurrentActiveShot}
								mediaOnCanvasIds={mediaOnCanvasIds}
								shotFeed={shotFeed}
							/>
						)}

						{
							mediaItem
							&& (
								AUDIO_TYPES.includes(mediaItem?.type ?? '')
								|| VID_TYPES.includes(mediaItem.type)
								|| mediaItem.type === 'hls'
								|| totalPages > 1
							)
							&& (
								<VideoThumbnailMouseover
									shotFeed={shotFeed}
									mediaItem={mediaItem}
									mouseIsOver={mouseIsOver}
									totalPages={totalPages}
									mediaName={mediaName}
									isCurrentActiveShot={isCurrentActiveShot}
								/>
							)
						}
					</div>
				</div>
			</div>
		</div>
	);
}
